import { postCommunityPostingCategoryAPI } from '@/src/apis/rest/communityPosting';
import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import RadioGroup from '@/src/components/atom/RadioGroup';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { toast } from '@/src/hooks/useToast';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { OPTIONS_CONST } from '@/src/constants/options';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
interface CommunityCategoryModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onSubmit: () => void;
  defaultCategories: {
    id?: number;
    categoryName: string;
    isActivated: boolean;
  }[];
}

export default function CommunityCategoryModal({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  defaultCategories,
  onSubmit,
}: CommunityCategoryModalProps) {
  // state
  const contentRef = useRef<HTMLDivElement>(null);
  const [categories, setCategories] = useState(defaultCategories);
  const isDisabled = useMemo(() => {
    const categoryNames = categories.map(item => item.categoryName);

    if (
      categoryNames.filter((item, idx) => categoryNames.indexOf(item) !== idx)
        .length > 0
    ) {
      return true;
    }

    if (categories.find(item => item.categoryName === '')) {
      return true;
    }

    return false;
  }, [categories]);
  const {
    Dialog: SaveDialog,
    isOpen: isSaveOpen,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // mutation
  const postCommunityPostingCategoryMutation = useMutation({
    mutationFn: postCommunityPostingCategoryAPI,
  });

  // event
  const handleCloseModal = () => {
    onOpenChangeProps(false);
  };
  const handleSubmit = () => {
    // submit
    postCommunityPostingCategoryMutation.mutate(
      { categoryList: categories },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          onSubmit();

          handleCloseModal();
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };
  const handleInputKeydown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number,
  ) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleRemove(categories.length - 1);

      const target = contentRef.current?.querySelectorAll(
        'input:not([type="radio"])',
      )?.[idx - 1];

      if (target) {
        (target as HTMLInputElement).focus();
      }
    }
    if (e.key === 'Backspace') {
      if (e.currentTarget.value === '') {
        e.preventDefault();
        handleRemove(idx);
        const target = contentRef.current?.querySelectorAll(
          'input:not([type="radio"])',
        )?.[idx - 1];

        if (target) {
          (target as HTMLInputElement).focus();
        }
      }
    }
  };
  const handleChange = (
    type: 'categoryName' | 'isActivated',
    value: string | boolean,
    changeIdx: number,
  ) => {
    setCategories(prev =>
      prev.map((item, idx) =>
        idx === changeIdx ? { ...item, [type]: value } : item,
      ),
    );
  };
  const handleRemove = (removeIdx: number) => {
    setCategories(prev => {
      return prev.length === 1
        ? [{ categoryName: '', isActivated: true }]
        : prev.filter((_, idx) => idx !== removeIdx);
    });
  };
  const handleAdd = () => {
    setCategories(prev => [...prev, { categoryName: '', isActivated: true }]);
  };

  // render
  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={openSaveDialog}
          disabled={isDisabled}
          isLoading={postCommunityPostingCategoryMutation.isPending}
        >
          {BUTTON_CONST.save}
        </Button>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: {
            width: 'calc(100vw - 24px)',
          },
          className: 'max-w-[995px]',
        }}
        title="게시글 카테고리 관리"
        footer={renderFooter()}
      >
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="flex flex-col gap-2"
        >
          <div className="flex h-[50vh] min-h-[200px] w-full flex-col gap-2">
            <article
              className="flex h-[full] flex-col rounded-lg border border-gray0"
              style={{ overflow: 'overlay' }}
            >
              <header className="bg-gray0">
                <div className="flex text-b14m text-gray7 [&>th]:border-r [&>th]:border-gray1">
                  <div className="flex w-[8%] items-center justify-center py-3">
                    No
                  </div>
                  <div className="flex flex-1 items-center justify-center py-3">
                    카테고리 명
                  </div>
                  <div className="flex w-[30%] items-center justify-center py-3">
                    사용여부
                  </div>
                  {/* <div className="flex w-[10%] items-center justify-center py-3">
                    {COMMUNITY_CATEGORY_MODAL.table.delete}
                  </div> */}
                </div>
              </header>
              <div className="flex flex-1 flex-col overflow-auto overflow-y-auto">
                {categories.map((item, idx) => (
                  <div key={idx.toString()} className="flex">
                    <div className="flex w-[8%] items-center justify-center">
                      <p className="flex h-11 items-center justify-center px-2 text-b14r text-gray9">
                        {(idx + 1).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex flex-1 items-center justify-center">
                      <div className="flex h-11 w-full items-center justify-center px-2">
                        <p>{item.categoryName}</p>
                      </div>
                    </div>
                    <div className="flex w-[30%] items-center justify-center">
                      <div className="flex h-11 w-full items-center justify-center px-2">
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.activated, value: 'true' },
                            {
                              label: OPTIONS_CONST.notActivated,
                              value: 'false',
                            },
                          ]}
                          rootProps={{
                            value: item.isActivated ? 'true' : 'false',
                            onValueChange: e => {
                              handleChange('isActivated', e === 'true', idx);
                            },
                          }}
                        />
                      </div>
                    </div>
                    {/* <div className="flex w-[10%] items-center justify-center">
                      <Button
                        buttonColor="red"
                        buttonSize="small"
                        disabled={!!item.id}
                        onClick={() => handleRemove(idx)}
                      >
                        삭제
                      </Button>
                    </div> */}
                  </div>
                ))}
              </div>
              {/* <footer>
                <div>
                  <div className="w-full p-2">
                    <Button
                      buttonColor="outline"
                      className="flex w-full items-center gap-2"
                      onClick={handleAdd}
                    >
                      <PlusIcon size={16} />{' '}
                      {COMMUNITY_CATEGORY_MODAL.button_BUTTON_CONST.add}
                    </Button>
                  </div>
                </div>
              </footer> */}
            </article>
          </div>
        </form>
      </Dialog>

      {/* 모달 */}
      {isSaveOpen && (
        <SaveDialog
          open={isSaveOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openSaveDialog();
            } else {
              closeSaveDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={postCommunityPostingCategoryMutation.isPending}
        />
      )}
    </>
  );
}
